import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiBell, FiCheck, FiClock, FiWifi, FiWifiOff } from "react-icons/fi";
import api from "../api";
import { getUserTimezone, formatClockInTimezone, getTimezoneMeta } from "../utils/timezone";

function TopStatusBanner({ sticky = true, floating = false, className = "" }) {
  const [connectionBadge, setConnectionBadge] = useState({ is_connected: false, mode: "unknown" });
  const [activeTimezone, setActiveTimezone] = useState(() => getUserTimezone());
  const [clockNow, setClockNow] = useState(() => formatClockInTimezone(new Date(), getUserTimezone()));
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellShake, setBellShake] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [accountData, setAccountData] = useState(() => {
    const fromStorage = String(localStorage.getItem("username") || "").trim();
    return {
      name: fromStorage || "Cuenta activa",
      email: fromStorage || "",
    };
  });

  useEffect(() => {
    const tick = () => setClockNow(formatClockInTimezone(new Date(), activeTimezone));
    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [activeTimezone]);

  useEffect(() => {
    const syncTimezone = () => setActiveTimezone(getUserTimezone());
    window.addEventListener('ws:timezone_changed', syncTimezone);
    window.addEventListener('storage', syncTimezone);
    return () => {
      window.removeEventListener('ws:timezone_changed', syncTimezone);
      window.removeEventListener('storage', syncTimezone);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadHeaderStatus = async () => {
      try {
        const [statusRes, profileRes] = await Promise.allSettled([
          api.get("integration/connection-status/"),
          api.get("user/profile/"),
        ]);

        if (cancelled) return;

        if (statusRes.status === "fulfilled") {
          const data = statusRes.value?.data || {};
          setConnectionBadge({
            is_connected: !!data?.is_connected,
            mode: String(data?.mode || "unknown"),
          });
        } else {
          setConnectionBadge({ is_connected: false, mode: "unknown" });
        }

        if (profileRes.status === "fulfilled") {
          const profile = profileRes.value?.data || {};
          const storedUser = String(localStorage.getItem("username") || "").trim();
          const accountName = String(profile?.name || "").trim() || storedUser || "Cuenta activa";
          const accountEmail = String(profile?.email || "").trim() || storedUser;
          setAccountData({ name: accountName, email: accountEmail });
        }
      } catch {
        if (!cancelled) {
          const fromStorage = String(localStorage.getItem("username") || "").trim();
          setAccountData({
            name: fromStorage || "Cuenta activa",
            email: fromStorage || "",
          });
          setConnectionBadge({ is_connected: false, mode: "unknown" });
        }
      }
    };

    loadHeaderStatus();
    const id = window.setInterval(loadHeaderStatus, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadNotifications = async () => {
      try {
        const { data } = await api.get("roadmap/notifications/", { params: { limit: 8 } });
        if (cancelled) return;
        const nextItems = Array.isArray(data?.results) ? data.results : [];
        const nextUnread = Number(data?.unread_count || 0);
        setNotifications(nextItems);
        setUnreadCount(nextUnread);
      } catch {
        if (!cancelled) {
          setNotifications([]);
          setUnreadCount(0);
        }
      }
    };

    loadNotifications();
    const id = window.setInterval(loadNotifications, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (unreadCount <= 0) return;
    setBellShake(true);
    const id = window.setTimeout(() => setBellShake(false), 900);
    return () => window.clearTimeout(id);
  }, [unreadCount]);

  useEffect(() => {
    const onDocClick = (evt) => {
      if (!notifRef.current) return;
      if (!notifRef.current.contains(evt.target)) setNotifOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const handleMarkRead = async () => {
    try {
      await api.post("roadmap/notifications/read-all/");
      setUnreadCount(0);
      setNotifications((prev) => prev.map((it) => ({ ...it, is_read: true })));
    } catch {
      // noop
    }
  };

  const connectionPillLabel = connectionBadge.is_connected ? "Conectado" : "No conectado";
  const connectionModeLabel =
    connectionBadge.mode === "coexistencia"
      ? "Meta"
      : connectionBadge.mode === "no_oficial"
        ? "Web"
        : "Pendiente";

  const initials = useMemo(() => {
    const source = String(accountData.name || accountData.email || "WS").trim();
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    if (parts.length <= 1) return source.slice(0, 2).toUpperCase() || "WS";
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }, [accountData]);

  const secondaryLine = useMemo(() => {
    const name = String(accountData.name || "").trim().toLowerCase();
    const email = String(accountData.email || "").trim();
    if (!email) return "";
    if (name && name === email.toLowerCase()) return "";
    return email;
  }, [accountData]);
  const timezoneMeta = useMemo(() => getTimezoneMeta(activeTimezone), [activeTimezone]);

  const placementClass = floating ? "fixed right-4 top-4" : (sticky ? "sticky top-3 z-30" : "");
  const placementStyle = floating ? { zIndex: 2147483000 } : undefined;

  const banner = (
    <div className={`${placementClass} ${className}`.trim()} style={placementStyle}>
      <div className="inline-flex max-w-[min(92vw,760px)] items-center gap-2 rounded-2xl border border-[#1f3048] bg-[#111c2c]/95 px-3 py-2 shadow-xl backdrop-blur-md">
        <div
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
            connectionBadge.is_connected
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
              : "border-amber-500/50 bg-amber-500/10 text-amber-300"
          }`}
        >
          {connectionBadge.is_connected ? <FiWifi size={12} /> : <FiWifiOff size={12} />}
          {connectionPillLabel}
          <span className="text-[10px] font-medium opacity-85">· {connectionModeLabel}</span>
        </div>

        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={() => setNotifOpen((v) => !v)}
            className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#2a3c5b] bg-[#16233a] text-[#c5d6ed] transition hover:bg-[#1b2c47]"
            title="Notificaciones"
          >
            <FiBell size={14} className={bellShake ? "animate-bounce" : ""} />
            {unreadCount > 0 && (
              <span className="absolute -right-1 -top-1 inline-flex min-w-[16px] items-center justify-center rounded-full bg-[#ff4d4f] px-1 text-[10px] font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-10 z-50 w-[320px] overflow-hidden rounded-2xl border border-[#22324b] bg-[#0f1828] shadow-2xl">
              <div className="flex items-center justify-between border-b border-[#22324b] px-3 py-2">
                <span className="text-xs font-semibold text-[#d3e0f2]">Notificaciones</span>
                <button
                  type="button"
                  onClick={handleMarkRead}
                  className="inline-flex items-center gap-1 rounded-md border border-[#314665] bg-[#15243c] px-2 py-1 text-[11px] text-[#bcd0eb] hover:bg-[#1b2e4b]"
                >
                  <FiCheck size={12} />
                  Marcar leidas
                </button>
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                {notifications.length === 0 && (
                  <p className="px-3 py-4 text-xs text-[#93a9c6]">Sin novedades por ahora.</p>
                )}
                {notifications.map((n) => (
                  <div key={n.id} className={`border-b border-[#1b2a42] px-3 py-2 ${n.is_read ? "opacity-75" : "bg-[#13243f]/70"}`}>
                    <p className="text-xs font-semibold text-[#e7f0ff]">{n.message}</p>
                    <p className="mt-1 text-[11px] text-[#8da5c7]">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="inline-flex min-w-[152px] items-center gap-2 rounded-lg border border-[#2a3c5b] bg-[#16233a] px-2.5 py-1.5">
          <FiClock size={13} className="text-[#8ca2c0]" />
          <span className="leading-tight">
            <span className="block text-[12px] font-semibold tracking-[0.08em] text-[#ff4d4f]">{clockNow}</span>
            <span className="block text-[10px] text-[#9fb2cf]">{timezoneMeta.short} · {timezoneMeta.country}</span>
          </span>
        </div>

        <div className="mx-1 h-7 w-px bg-[#2a3c5b]" />

        <div className="inline-flex min-w-[170px] items-center gap-2.5 rounded-xl border border-[#2a3c5b] bg-[#16233a] px-2.5 py-1.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-xs font-bold text-emerald-300">
            {initials}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-xs font-semibold leading-tight text-[#e2ebf9]">{accountData.name || "Cuenta activa"}</span>
            {secondaryLine && (
              <span className="block truncate text-[11px] leading-tight text-[#9fb2cf]">{secondaryLine}</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );

  if (floating && typeof document !== "undefined") {
    return createPortal(banner, document.body);
  }

  return banner;
}

export default TopStatusBanner;